import { forwardRef } from "react";
import { cn } from "../lib/cn";
import { CheckIcon } from "../lib/icons";

/**
 * Stepper — progress through a fixed sequence of steps.
 *
 *   <Stepper
 *     steps={["Upload", "Map columns", "Review", "Import"]}
 *     activeStep={1}
 *     onStepClick={setStep}
 *   />
 *
 * Mirrors MUI Stepper: completed steps render a primary-filled check circle,
 * the current step an outlined primary circle, upcoming steps a muted circle.
 * Steps become buttons when onStepClick is provided. Vertical orientation
 * stacks the same circles with a connector spine.
 */

type StepState = "completed" | "current" | "upcoming";

export interface StepperProps {
  /** Step labels, in order. */
  steps: string[];
  /** Index of the current step; earlier steps render as completed. */
  activeStep: number;
  orientation?: "horizontal" | "vertical";
  /** Renders the steps as buttons that call back with the clicked index. */
  onStepClick?: (index: number) => void;
  className?: string;
}

const circleClasses: Record<StepState, string> = {
  completed: "bg-surface-primary text-on-primary",
  current: "border-2 border-surface-primary text-content-accent",
  upcoming: "border border-border-strong bg-surface-raised text-content-secondary",
};

const labelClasses: Record<StepState, string> = {
  completed: "text-content-primary",
  current: "font-medium text-content-primary",
  upcoming: "text-content-secondary",
};

const connectorDone = "bg-surface-primary";
const connectorTodo = "bg-border-strong";

function stepState(index: number, activeStep: number): StepState {
  if (index < activeStep) {
    return "completed";
  }
  return index === activeStep ? "current" : "upcoming";
}

function StepperCircle({ state, index }: { state: StepState; index: number }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-full type-body-md font-semibold tabular-nums",
        circleClasses[state],
      )}
    >
      {state === "completed" ? <CheckIcon className="size-4" /> : index + 1}
    </span>
  );
}

function HorizontalStep({
  label,
  index,
  state,
  isLast,
  onClick,
}: {
  label: string;
  index: number;
  state: StepState;
  isLast: boolean;
  onClick?: () => void;
}) {
  const content = (
    <>
      <StepperCircle state={state} index={index} />
      <span className={cn("ml-2 min-w-0 truncate type-body-md", labelClasses[state])}>
        {label}
      </span>
    </>
  );
  return (
    <li
      aria-current={state === "current" ? "step" : undefined}
      className={cn("flex min-w-0 items-center", !isLast && "flex-1")}
    >
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="flex min-w-0 cursor-default items-center rounded-md py-0.5 pr-1 outline-none focus-visible:ring-2 focus-visible:ring-border-focused focus-visible:ring-offset-2 focus-visible:ring-offset-surface-canvas"
        >
          {content}
        </button>
      ) : (
        <div className="flex min-w-0 items-center py-0.5">{content}</div>
      )}
      {isLast ? null : (
        <span
          aria-hidden="true"
          className={cn(
            "mx-3 h-px min-w-6 grow",
            state === "completed" ? connectorDone : connectorTodo,
          )}
        />
      )}
    </li>
  );
}

function VerticalStep({
  label,
  index,
  state,
  isLast,
  onClick,
}: {
  label: string;
  index: number;
  state: StepState;
  isLast: boolean;
  onClick?: () => void;
}) {
  const content = (
    <span className={cn("type-body-md", labelClasses[state])}>{label}</span>
  );
  return (
    <li
      aria-current={state === "current" ? "step" : undefined}
      className="flex items-start gap-3"
    >
      <span aria-hidden="true" className="flex flex-col items-center self-stretch">
        <StepperCircle state={state} index={index} />
        {isLast ? null : (
          <span
            className={cn(
              "my-2 w-px grow",
              state === "completed" ? connectorDone : connectorTodo,
            )}
          />
        )}
      </span>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="mt-1.5 min-w-0 cursor-default rounded-md px-1 py-0.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-border-focused focus-visible:ring-offset-2 focus-visible:ring-offset-surface-canvas"
        >
          {content}
        </button>
      ) : (
        <div className="mt-1.5 min-w-0 px-1 py-0.5">{content}</div>
      )}
    </li>
  );
}

export const Stepper = forwardRef<HTMLOListElement, StepperProps>(
  (
    { steps, activeStep, orientation = "horizontal", onStepClick, className },
    ref,
  ) => {
    return (
      <ol
        ref={ref}
        data-slot="stepper"
        data-orientation={orientation}
        className={cn(
          orientation === "horizontal" ? "flex w-full items-center" : "flex flex-col",
          className,
        )}
      >
        {steps.map((label, index) => {
          const props = {
            label,
            index,
            state: stepState(index, activeStep),
            isLast: index === steps.length - 1,
            onClick: onStepClick ? () => onStepClick(index) : undefined,
          };
          return orientation === "horizontal" ? (
            <HorizontalStep key={label} {...props} />
          ) : (
            <VerticalStep key={label} {...props} />
          );
        })}
      </ol>
    );
  },
);
Stepper.displayName = "Stepper";
